import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ChevronRight } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import FacilitatorLayout from '@/components/FacilitatorLayout';
import { Header } from '@/components/prosci/Header';

// Import the new shared component
import AssessmentSummaryReportDisplay from '../components/AssessmentSummaryReportDisplay';

// Helper function to determine score level for detailed assessments
const getScoreLevel = (score) => {
  if (score >= 46) return "Extending";
  if (score >= 40) return "Proficient";
  if (score >= 32) return "Developing";
  if (score >= 22) return "Emerging";
  if (score >= 10) return "Initiating";
  return "Unknown"; // Should not happen with scores 10-50
};

// New helper function to translate detailed score to text label
const getDetailedScoreLabel = (score) => {
  // Round the average score for labeling purposes
  const roundedScore = score ? Math.round(score) : 0;
  switch (roundedScore) {
    case 1: return "Not Applied";
    case 2: return "Minimally Applied";
    case 3: return "Partially Applied";
    case 4: return "Mostly Applied";
    case 5: return "Fully Applied";
    default: return "Not Answered";
  }
};

// Helper function to get badge Tailwind classes for individual factor labels
const getFacilitatorBadgeStyle = (factor, assessmentType) => {
  const baseClasses = "px-2 py-0.5 rounded-full text-xs font-semibold";
  let bgColorClass = 'bg-gray-200'; // Default for Not Answered / Neutral
  let textColorClass = 'text-gray-700';

  if (assessmentType === 'full_detailed') {
    const score = factor.avg_score;
    if (score == null || score === 0) {
       // Keep default gray
    } else if (score < 1.5) { bgColorClass = 'bg-score-1'; textColorClass = 'text-score-1-foreground'; }
    else if (score < 2.5) { bgColorClass = 'bg-score-2'; textColorClass = 'text-score-2-foreground'; }
    else if (score < 3.5) { bgColorClass = 'bg-score-3'; textColorClass = 'text-score-3-foreground'; }
    else if (score < 4.5) { bgColorClass = 'bg-score-4'; textColorClass = 'text-score-4-foreground'; }
    else { bgColorClass = 'bg-score-5'; textColorClass = 'text-score-5-foreground'; }
  } else { // Simple Assessment - Use avg_score to determine S/O/Mixed
    const avgScore = factor.avg_score;
    if (avgScore == null) { // Not answered
       bgColorClass = 'bg-gray-200'; textColorClass = 'text-gray-700';
    } else if (avgScore >= 4) { // Strength
       bgColorClass = 'bg-score-5'; textColorClass = 'text-score-5-foreground';
    } else if (avgScore <= 2) { // Opportunity
       bgColorClass = 'bg-score-3'; textColorClass = 'text-score-3-foreground';
    } else { // Mixed (avg score is > 2 and < 4)
       bgColorClass = 'bg-gray-400'; textColorClass = 'text-gray-100';
    }
  }
  return `${baseClasses} ${bgColorClass} ${textColorClass}`;
};

// Helper function to get background color class for the grid squares
const getGridBgClass = (avgScore) => {
  if (avgScore == null || avgScore === 0) return 'bg-gray-200';
  if (avgScore < 1.5) return 'bg-score-1';
  if (avgScore < 2.5) return 'bg-score-2';
  if (avgScore < 3.5) return 'bg-score-3';
  if (avgScore < 4.5) return 'bg-score-4';
  return 'bg-score-5';
};

const FacilitatorAssessmentReport = ({
  assessmentSession,
  report,
  updateUrl,
  generateUrl,
  downloadUrl,
  csrfToken,
  currentUser
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [facilitatorNotes, setFacilitatorNotes] = useState(report.facilitatorNotes || '');
  const [agreedActions, setAgreedActions] = useState(report.agreedActions || '');
  const [selectedViewIdentifier, setSelectedViewIdentifier] = useState('all');

  // Calculate response statistics (overall for the session, does not change with viewIdentifier)
  const statistics = useMemo(() => {
    const allSessionResponses = assessmentSession.assessmentResponses || [];
    const structure = assessmentSession.structure || [];
    const assessmentType = assessmentSession.assessmentType;

    const totalResponses = allSessionResponses.length; // Total individual answers

    // 1. Group scores by factorId and calculate averages for all factors
    const factorScoresMap = allSessionResponses.reduce((acc, response) => {
      const scoreValue = response.response ? Number(response.response) : NaN;
      if (!isNaN(scoreValue) && scoreValue > 0) {
        if (!acc[response.factorId]) {
          acc[response.factorId] = { sum: 0, count: 0 };
        }
        acc[response.factorId].sum += scoreValue;
        acc[response.factorId].count++;
      }
      return acc;
    }, {});

    const factorAverages = {};
    for (const factorId in factorScoresMap) {
      factorAverages[factorId] = factorScoresMap[factorId].sum / factorScoresMap[factorId].count;
    }
    const uniqueFactorsAssessed = Object.keys(factorAverages).length;

    if (assessmentType === 'full_detailed') {
      let totalOverallAssessmentScore = 0;
      structure.forEach(contributor => {
        let sectionTotalScore = 0;
        (contributor.factors || []).forEach(factor => {
          sectionTotalScore += (factorAverages[factor.id] || 0);
        });
        totalOverallAssessmentScore += Math.round(sectionTotalScore);
      });

      const getOverallLevelDetails = (score) => {
        if (score >= 361) return {
          name: "Extending",
          description: "Best practices are embedded and Reinforced to sustain change outcomes. There is a shared expectation that these practices should be used on all change initiatives, and lessons learned are captured and shared to strengthen organizational change maturity.",
          scoreBgClass: 'bg-score-5', scoreTextClass: 'text-score-5-foreground' };
        if (score >= 313) return {
          name: "Proficient",
          description: "There is a demonstrated Ability to apply change management practices consistently and effectively across all impacted groups. Best practices are adapted based on context and refined through experience.",
          scoreBgClass: 'bg-score-4', scoreTextClass: 'text-score-4-foreground' };
        if (score >= 249) return {
          name: "Developing",
          description: "There is increasing Knowledge of how to apply change management effectively. Best practices are used more consistently, but execution still varies across impacted groups, and some gaps remain.",
          scoreBgClass: 'bg-score-3', scoreTextClass: 'text-score-3-foreground' };
        if (score >= 169) return {
          name: "Emerging",
          description: "There is growing awareness of the need for change management and an increasing Desire to engage. Some best practices are applied, but efforts are often reactive, inconsistent, or driven by external expectations.",
          scoreBgClass: 'bg-score-2', scoreTextClass: 'text-score-2-foreground' };
        if (score >= 80) return {
          name: "Initiating",
          description: "There is little Awareness of the need for change management or how to apply it. Best practices are not applied, or the application is unplanned and inconsistent.",
          scoreBgClass: 'bg-score-1', scoreTextClass: 'text-score-1-foreground' };
        if (score > 0) return { 
          name: "Below Initiating", 
          description: "The score is below the threshold for Initiating.", 
          scoreBgClass: 'bg-gray-200', scoreTextClass: 'text-gray-700' }; 
        return { 
          name: "Unknown", 
          description: "Overall score could not be determined.", 
          scoreBgClass: 'bg-gray-200', scoreTextClass: 'text-gray-700' };
      };
      const overallLevelDetails = getOverallLevelDetails(totalOverallAssessmentScore);

      return {
        totalResponses,
        uniqueFactorsAssessed,
        totalOverallAssessmentScore,
        overallLevelDetails,
        assessmentType
      };
    } else { // For quick_interactive or other types
      let strengthFactorsCount = 0;
      let opportunityFactorsCount = 0;
      let mixedFactorsCount = 0;

      for (const factorId in factorAverages) {
        const avgScore = factorAverages[factorId];
        if (avgScore >= 3.5) { // Threshold for Strength
          strengthFactorsCount++;
        } else if (avgScore <= 2.5) { // Threshold for Opportunity
          opportunityFactorsCount++;
        } else { // Mixed
          mixedFactorsCount++;
        }
      }
      return {
        totalResponses,
        uniqueFactorsAssessed,
        strengthFactorsCount,
        opportunityFactorsCount,
        mixedFactorsCount,
        assessmentType
      };
    }
  }, [assessmentSession.assessmentResponses, assessmentSession.structure, assessmentSession.assessmentType]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          report: {
            facilitator_notes: facilitatorNotes,
            agreed_actions: agreedActions
          }
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.errors?.join(', ') || 'Failed to save report');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(generateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to generate report');
      }

      // Redirect to download if generation was successful
      window.location.href = downloadUrl;
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Helper to compute factor stats and contributor summaries (mimic participant summary logic)
  const computeFactorStatsAndSummaries = (structure, allSessionResponses, assessmentType, viewIdentifier) => {
    const responsesForView = (viewIdentifier && viewIdentifier !== 'all')
      ? allSessionResponses.filter(r => r.participantIdentifier === viewIdentifier) // Ensure field name matches your data
      : allSessionResponses;

    // Build factor_stats
    const factor_stats = (structure || []).flatMap(contributor =>
      (contributor.factors || []).map(factor => {
        let factorScores, currentFactorResponses;
        let avg_score, response_count, hasStrength, hasOpportunity;

        if (viewIdentifier && viewIdentifier !== 'all') {
          // Single participant view
          currentFactorResponses = responsesForView.filter(r => r.factorId === factor.id && r.response && !isNaN(Number(r.response)));
          factorScores = currentFactorResponses.map(r => Number(r.response)).filter(n => n > 0);
          avg_score = factorScores.length > 0 ? factorScores[0] : null; // Direct score
          response_count = factorScores.length; // 0 or 1
          hasStrength = factorScores.length > 0 && factorScores[0] >= 4;
          hasOpportunity = factorScores.length > 0 && factorScores[0] <= 2;
        } else {
          // All participants view (original logic for calculating overall average for the factor)
          currentFactorResponses = allSessionResponses.filter(r => r.factorId === factor.id && r.response && !isNaN(Number(r.response)));
          factorScores = currentFactorResponses.map(r => Number(r.response)).filter(n => n > 0);
          avg_score = factorScores.length > 0 ? factorScores.reduce((a, b) => a + b, 0) / factorScores.length : null;
          response_count = factorScores.length;
          hasStrength = factorScores.some(s => s === 4 || s === 5); // Any strength response
          hasOpportunity = factorScores.some(s => s === 1 || s === 2); // Any opportunity response
        }

        return {
          key: factor.id,
          text: factor.text,
          contributor_key: contributor.id,
          avg_score, // If single view, this is their direct score. If all, it's the overall factor average.
          response_count, // If single view, 0 or 1. If all, total for factor.
          scores: factorScores, // Raw scores for this view (all or single participant)
          hasStrength, // Specific to this view
          hasOpportunity // Specific to this view
        };
      })
    );

    // Build contributor_summaries with detailed stats
    const contributor_summaries = (structure || []).map(contributor => {
      const factorsInSection = factor_stats.filter(f => f.contributor_key === contributor.id);
      const total_factors = factorsInSection.length;
      const answered_factors = factorsInSection.filter(f => f.response_count > 0).length;

      let section_total_score = 0; // For detailed assessment
      let section_strength_count = 0; // For simple assessment
      let section_opportunity_count = 0; // For simple assessment
      let section_mixed_count = 0; // For simple assessment (all view only)

      if (assessmentType === 'full_detailed') {
        // avg_score is already view-specific (either direct score or overall factor average)
        section_total_score = Math.round(
          factorsInSection.reduce((sum, factor) => sum + (factor.avg_score || 0), 0)
        );
      } else { // For quick_interactive/simple assessments
        if (viewIdentifier && viewIdentifier !== 'all') {
          // Single participant view: S/O based on their direct responses for factors in this section
          factorsInSection.forEach(factor => {
            if (factor.hasStrength) section_strength_count++;
            if (factor.hasOpportunity) section_opportunity_count++;
            // No mixed for single participant on a per-factor basis leading to section mixed count
          });
        } else {
          // All participants view: S/O/M for section based on *overall factor averages*
          factorsInSection.forEach(factor => {
            if (factor.response_count > 0) { // Ensure factor was answered by at least one person
              const overallFactorAvg = factor.avg_score; // This is the overall average for the factor
              // Using new thresholds: S >= 4, O <= 2, M > 2 and < 4
              if (overallFactorAvg >= 4) { // Factor is a strength
                section_strength_count++;
              } else if (overallFactorAvg <= 2) { // Factor is an opportunity
                section_opportunity_count++;
              } else { // Factor is mixed (between 2 and 4)
                section_mixed_count++;
              }
            }
          });
        }
      }

      return {
        key: contributor.id,
        name: contributor.name,
        total_factors,
        answered_factors,
        isComplete: answered_factors > 0 && answered_factors === total_factors,
        total_score: section_total_score, // Populated for detailed, avg for section
        strength_count: section_strength_count,
        opportunity_count: section_opportunity_count,
        mixed_count: section_mixed_count // Will be 0 for single participant view in simple assessment
      };
    });

    return { factor_stats, contributor_summaries };
  };

  const { factor_stats, contributor_summaries } = computeFactorStatsAndSummaries(
    assessmentSession.structure || [],
    assessmentSession.assessmentResponses || [],
    assessmentSession.assessmentType,
    selectedViewIdentifier
  );

  const getGridColor = (avgScore) => {
    if (avgScore == null || avgScore === 0) return 'bg-gray-200';
    if (avgScore < 1.5) return 'bg-rose-100';
    if (avgScore < 2.5) return 'bg-rose-200';
    if (avgScore < 3.5) return 'bg-lime-100';
    if (avgScore < 4.5) return 'bg-lime-200';
    return 'bg-lime-300';
  };

  // Helper function to get the label for a factor badge (passed as prop)
  const getFacilitatorFactorLabel = (factor, assessmentType) => {
    if (assessmentType === 'full_detailed') {
      return getDetailedScoreLabel(factor.avg_score);
    } else { // Simple Assessment
      const hasStrength = factor?.hasStrength;
      const hasOpportunity = factor?.hasOpportunity;
      const avgScore = factor?.avg_score;

      if (avgScore == null) {
        return "Not Answered";
      }
      
      // Use average score thresholds for consistency
      if (avgScore >= 4) {
        return "Strength";
      } else if (avgScore <= 2) {
        return "Opportunity";
      } else {
        // Mixed: between 2 and 4
        if (assessmentType === 'quick_interactive') {
          if (avgScore < 3) {
            return "Mixed / O";
          } else if (avgScore > 3) {
            return "Mixed / S";
          }
        }
        return "Mixed";
      }
    }
  };

  // Helper function to get the subtitle JSX for a section (passed as prop)
  const getFacilitatorSectionSubtitle = (summary, assessmentType) => {
    const baseBadgeClasses = "px-2 py-0.5 rounded-full text-xs font-semibold font-sans mr-1";
    const isComplete = summary.isComplete; // Assuming this is calculated in computeFactorStatsAndSummaries
    const totalFactors = summary.total_factors;
    const answeredFactors = summary.answered_factors;

    if (isComplete && totalFactors > 0) {
      if (assessmentType === 'full_detailed') {
        const score = summary.total_score;
        const level = getScoreLevel(score);
        const text = `Score: ${score} (${level})`;
        let scoreBgColor = 'bg-gray-200';
        let scoreTextColor = 'text-gray-700';
        if (score >= 46)      { scoreBgColor = 'bg-score-5'; scoreTextColor = 'text-score-5-foreground'; }
        else if (score >= 40) { scoreBgColor = 'bg-score-4'; scoreTextColor = 'text-score-4-foreground'; }
        else if (score >= 32) { scoreBgColor = 'bg-score-3'; scoreTextColor = 'text-score-3-foreground'; }
        else if (score >= 22) { scoreBgColor = 'bg-score-2'; scoreTextColor = 'text-score-2-foreground'; }
        else if (score >= 10) { scoreBgColor = 'bg-score-1'; scoreTextColor = 'text-score-1-foreground'; }
        return (
          <span className={cn(baseBadgeClasses, scoreBgColor, scoreTextColor)}>
            {text}
          </span>
        );
      } else { // Simple assessment type - Render 3 badges
        const S = summary.strength_count;
        const O = summary.opportunity_count;
        const M = summary.mixed_count;
        // Ensure counts are numbers before rendering
        if (typeof S === 'number' && typeof O === 'number' && typeof M === 'number') {
            return (
              <>
                <span className={cn(baseBadgeClasses, 'bg-score-4', 'text-score-4-foreground')}>
                  S: {S}
                </span>
                <span className={cn(baseBadgeClasses, 'bg-score-2', 'text-score-2-foreground')}>
                  O: {O}
                </span>
                <span className={cn(baseBadgeClasses, 'bg-gray-200', 'text-gray-700')}>
                  M: {M}
                </span>
              </>
            );
        } else {
           // Fallback if counts are missing/invalid
           return <span className={cn(baseBadgeClasses, 'bg-gray-200', 'text-gray-700')}>Complete</span>;
        }
      }
    } else if (answeredFactors > 0) {
      return <span className={cn(baseBadgeClasses, 'bg-gray-200', 'text-gray-700')}>Incomplete</span>;
    } else if (totalFactors === 0) {
      return <span className={cn(baseBadgeClasses, 'bg-gray-100', 'text-gray-500')}>No Change Success Assessment Factors</span>;
    } else {
      return <span className={cn(baseBadgeClasses, 'bg-gray-200', 'text-gray-700')}>Not Started</span>;
    }
  };

  // Get unique participant identifiers for the dropdown
  const uniqueParticipantIdentifiers = useMemo(() => {
    const ids = new Set((assessmentSession.assessmentResponses || []).map(r => r.participantIdentifier).filter(Boolean));
    return Array.from(ids);
  }, [assessmentSession.assessmentResponses]);

  return (
    <FacilitatorLayout userName={currentUser?.name || 'Facilitator'}>
      <Header
        title="Assessment Report"
        size="small"
        breadcrumb={
          <nav className="flex items-center gap-1.5 text-sm">
            <a href="/facilitator/assessment_sessions">Sessions</a>
            <ChevronRight className="h-3 w-3" />
            <a href={`/facilitator/assessment_sessions/${assessmentSession.id}`}>
              {assessmentSession.name || 'Session'}
            </a>
            <ChevronRight className="h-3 w-3" />
            <span>Report</span>
          </nav>
        }
      >
        <Button variant="lilac" onClick={handleGenerate} disabled={isGenerating}>
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating PDF...
            </>
          ) : (
            'Generate PDF'
          )}
        </Button>
      </Header>
      <div className="container mx-auto p-4 md:p-6 space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* View Selector Dropdown */}
        <div className="my-4 flex items-center">
          <Label htmlFor="facilitatorViewSelector" className="mr-2 font-medium">
            Show data for:
          </Label>
          <Select value={selectedViewIdentifier} onValueChange={setSelectedViewIdentifier}>
            <SelectTrigger id="facilitatorViewSelector" className="w-[300px]">
              <SelectValue placeholder="Select view" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups...</SelectItem>
              {uniqueParticipantIdentifiers.map((id) => (
                <SelectItem key={id} value={id}>
                  {id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Full-width Assessment Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Assessment Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-xl mb-2">Project Details</h3>
                <p>Name: {assessmentSession.name}</p>
              </div>
              <div>
                <h3 className="text-xl mb-2">Response Statistics</h3>
                <div className="space-y-2">
                  <p>Total Respondents: {statistics.totalResponses}</p>
                  <p>Unique Practices Assessed: {statistics.uniqueFactorsAssessed}</p>
                  {statistics.assessmentType === 'full_detailed' ? (
                    <div className="mt-1">
                      <p>
                        <span className={cn('px-2 py-0.5 rounded-full text-sm font-semibold', statistics.overallLevelDetails.scoreBgClass, statistics.overallLevelDetails.scoreTextClass)}>
                          Overall Score: {statistics.totalOverallAssessmentScore} ({statistics.overallLevelDetails.name})
                        </span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1 italic">{statistics.overallLevelDetails.description}</p>
                    </div>
                  ) : (
                    <>
                      <p>Total Strengths: {statistics.strengthFactorsCount}</p>
                      <p>Total Opportunities: {statistics.opportunityFactorsCount}</p>
                      <p>Mixed Responses: {statistics.mixedFactorsCount}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Two-column layout for grid and detailed scores */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Left column - Visual Grid */}
          <Card>
            <CardHeader>
              <CardTitle>Visual Summary Grid</CardTitle>
            </CardHeader>
            <CardContent>
              <AssessmentSummaryReportDisplay
                structure={assessmentSession.structure || []}
                factorStats={factor_stats || []} // Already computed
                contributorSummaries={contributor_summaries || []} // Already computed
                assessmentType={assessmentSession.assessmentType}
                getFactorBadgeStyle={getFacilitatorBadgeStyle} // Pass the function
                getFactorLabel={getFacilitatorFactorLabel} // Pass the function
                getSectionSubtitleElement={getFacilitatorSectionSubtitle} // Pass the function
                currentViewIdentifier={selectedViewIdentifier} // Pass current_view_identifier
                displayMode="grid" // Only show the grid
              />
            </CardContent>
          </Card>

          {/* Right column - Detailed Contributor Scores */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Contributor Scores</CardTitle>
            </CardHeader>
            <CardContent>
              <AssessmentSummaryReportDisplay
                structure={assessmentSession.structure || []}
                factorStats={factor_stats || []} // Already computed
                contributorSummaries={contributor_summaries || []} // Already computed
                assessmentType={assessmentSession.assessmentType}
                getFactorBadgeStyle={getFacilitatorBadgeStyle} // Pass the function
                getFactorLabel={getFacilitatorFactorLabel} // Pass the function
                getSectionSubtitleElement={getFacilitatorSectionSubtitle} // Pass the function
                currentViewIdentifier={selectedViewIdentifier} // Pass current_view_identifier
                displayMode="accordion" // Only show the accordion
              />
            </CardContent>
          </Card>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Facilitator Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea value={facilitatorNotes} onChange={(e) => setFacilitatorNotes(e.target.value)} placeholder="Enter your notes about the assessment results..." className="min-h-[200px]" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Agreed Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea value={agreedActions} onChange={(e) => setAgreedActions(e.target.value)} placeholder="List the agreed upon actions..." className="min-h-[200px]" />
            </CardContent>
          </Card>
        </div>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </FacilitatorLayout>
  )
};

FacilitatorAssessmentReport.propTypes = {
  assessmentSession: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    assessmentType: PropTypes.string,
    status: PropTypes.string,
    assessmentResponses: PropTypes.arrayOf(PropTypes.shape({
      factorId: PropTypes.string,
      response: PropTypes.string
    })),
    structure: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      factors: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.string
      }))
    }))
  }).isRequired,
  report: PropTypes.shape({
    id: PropTypes.number,
    facilitatorNotes: PropTypes.string,
    agreedActions: PropTypes.string,
    status: PropTypes.string,
    generatedAt: PropTypes.string,
    pdfUrl: PropTypes.string
  }).isRequired,
  updateUrl: PropTypes.string.isRequired,
  generateUrl: PropTypes.string.isRequired,
  downloadUrl: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  currentUser: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string
  })
};

export default FacilitatorAssessmentReport; 